import { Navigate, Route, Routes, useNavigate, useLocation } from "react-router-dom";
import { useCallback } from "react";
import { ThemeProvider, useColorScheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { createBrandTheme } from "brand";
import {
  CognitoAuthProvider,
  ProtectedRoute,
  CallbackPage,
  LogoutPage,
  LoggedOutPage,
  ActiveOrgProvider,
  ActiveShowProvider,
  useActiveOrg,
  BuildBadge,
  UsaBadge,
} from "common-fe";
import { OrganizationApi, Configuration } from "account-common";
import { useAuth } from "react-oidc-context";
import { ScoreProvider, useScoreContext } from "./context/ScoreContext";
import { useScoreApi, unwrap } from "./api/scoreApi";
import InitialSetup from "./components/InitialSetup";
import ClassList from "./components/ClassList";
import RideList from "./components/RideList";
import ScoringForm from "./components/ScoringForm";
import ClassGrid from "./components/ClassGrid";
import Outbox from "./components/Outbox";
import ReloadPrompt from "./components/ReloadPrompt";
import { useInactivityRedirect } from "./hooks/useInactivityRedirect";
import { usePresenceHeartbeat } from "./hooks/usePresenceHeartbeat";
import { useRequestStoragePersistence } from "./hooks/useRequestStoragePersistence";
import { useStationSnapshotUpload } from "./hooks/useStationSnapshotUpload";

const ACCOUNT_API_BASE_URL = import.meta.env.VITE_ACCOUNT_API_BASE_URL;

// Inject the org-list fetcher (built from the generated account-common client)
// into the shared ActiveOrgProvider so scribe sends the active-org X-Org-Id
// header on every score-api call. Without this the API falls back to the token's
// first org (Competitors) and the scribe sees no shows/classes (card #479).
// Same pattern as scoreadmin-fe. A single-org scribe is auto-selected by the
// provider; super/multi-org users pick the org in InitialSetup.
const ScribeActiveOrgProvider = ({ children }) => {
  const auth = useAuth();
  const accessToken = auth.user?.access_token;
  const listOrgs = useCallback(
    // #3005 — forward ActiveOrgProvider's AbortController signal into the
    // real network call so an unmount actually cancels the request.
    async (isSuper, signal) => {
      if (!accessToken) return [];
      const api = new OrganizationApi(
        new Configuration({ basePath: ACCOUNT_API_BASE_URL, accessToken })
      );
      const res = isSuper
        ? await api.listAllOrganizations(true, { signal }) // enabledOnly (#2074): switcher shows only superadminEnabled orgs
        : await api.listOrganizations({ signal });
      return res.data?.status === "success" && Array.isArray(res.data.data)
        ? res.data.data
        : [];
    },
    [accessToken]
  );
  return <ActiveOrgProvider listOrgs={listOrgs}>{children}</ActiveOrgProvider>;
};

// #521 — shared active-show (org-scoped) context. scribe has NO app-bar
// ShowSwitcher (changing the show is PIN-gated through InitialSetup, see
// TopBar's own #521/#628 comment) — the scribing session (ScoreContext,
// restored from localStorage) is the source of truth, and TopBar pushes it
// INTO this context (one-way) purely so anything else keyed off
// useActiveShow() stays scoped to it. `shows`/`filterActive` from this
// provider are therefore never rendered as a picker in scribe: the real
// show picker scribe users see is InitialSetup.jsx, fed by useShows() (a
// cache-first IndexedDB read, not this context) — #2222's date-window
// filter is applied there directly (see InitialSetup.jsx's own #2222
// comment), not via this provider's filterActive.
const ScribeActiveShowProvider = ({ children }) => {
  const scoreApi = useScoreApi();
  const { activeOrgId } = useActiveOrg();
  const listShows = useCallback(
    // #3005 — forward ActiveShowProvider's AbortController signal into the
    // real network call so an unmount actually cancels the request instead
    // of only silencing the resulting setState.
    async (signal) => unwrap(await scoreApi.shows.listShows({ signal })),
    [scoreApi]
  );
  return (
    <ActiveShowProvider listShows={listShows} orgId={activeOrgId}>
      {children}
    </ActiveShowProvider>
  );
};

// Authenticated landing. R1-UI-001's "skip setup, jump straight to the
// assigned class" shortcut depended on mock scribe-assignment data; scribe
// assignments aren't in the API yet (admin write path is #365), so an
// authenticated scribe lands on /setup to pick show + position manually, or
// on /classes if a session is already active.
function LandingRoute() {
  const { isSessionReady } = useScoreContext();
  return <Navigate to={isSessionReady ? "/classes" : "/setup"} replace />;
}

function SessionRequiredRoute({ children }) {
  const { isSessionReady } = useScoreContext();
  if (!isSessionReady) {
    return <Navigate to="/setup" replace />;
  }
  return children;
}

// #2364 — mounts the presence heartbeat sender exactly once, inside
// ScoreProvider (it needs useScoreContext for the active show + useScoreApi
// for the client), independent of route so the interval survives moving
// between classes/rides — only usePresenceHeartbeat's own read of
// store/presenceLocation changes per screen. Renders nothing.
function PresenceHeartbeat() {
  usePresenceHeartbeat();
  return null;
}

// #2829 — mounts the storage-persistence startup check exactly once (same
// pattern as PresenceHeartbeat above): the hook itself gates on isAuthenticated,
// so this just needs to be mounted somewhere that survives route changes.
// Renders nothing; the result surfaces via StoragePersistenceChip (TopBar).
function StoragePersistenceGate() {
  useRequestStoragePersistence();
  return null;
}

// #2823 — mounts the background station-snapshot uploader exactly once,
// same pattern as PresenceHeartbeat above: it needs useScoreContext for the
// active show and useScoreApi for the client, independent of route so its
// debounce/floor timers survive moving between classes/rides. Renders
// nothing.
function StationSnapshotUpload() {
  useStationSnapshotUpload();
  return null;
}

// Protected + session-gated wrapper for the scoring screens.
function Scoped({ children }) {
  return (
    <ProtectedRoute>
      <SessionRequiredRoute>{children}</SessionRequiredRoute>
    </ProtectedRoute>
  );
}

function AppContent({ cognitoAuthConfig }) {
  const navigate = useNavigate();
  const location = useLocation();
  useColorScheme();
  // R1-UI-004 — global inactivity watcher. Paused on /setup by the hook
  // itself; otherwise routes to /setup after the timeout.
  useInactivityRedirect();

  return (
    <CognitoAuthProvider cognitoAuthConfig={cognitoAuthConfig} location={location}>
      <ScribeActiveOrgProvider>
        <ScribeActiveShowProvider>
        <ScoreProvider>
          <PresenceHeartbeat />
          <StoragePersistenceGate />
          <StationSnapshotUpload />
          <Routes>
            <Route
              path="/callback"
              element={<CallbackPage navigateFunction={navigate} location={location} />}
            />
            <Route path="/logout" element={<LogoutPage navigateFunction={navigate} />} />
            <Route path="/logged-out" element={<LoggedOutPage />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <LandingRoute />
                </ProtectedRoute>
              }
            />
            <Route
              path="/setup"
              element={
                <ProtectedRoute>
                  <InitialSetup />
                </ProtectedRoute>
              }
            />
            <Route path="/classes" element={<Scoped><ClassList /></Scoped>} />
            <Route path="/classes/:classId" element={<Scoped><RideList /></Scoped>} />
            <Route path="/classes/:classId/grid" element={<Scoped><ClassGrid /></Scoped>} />
            <Route path="/rides/:rideId/score" element={<Scoped><ScoringForm /></Scoped>} />
            {/* #2826 -- every sheet this station has touched for the show,
                shipped and unshipped. #3016 wired SyncStatus's own chip tap
                to navigate here, and #3093 added the always-visible TopBar
                link, so it's reachable from every AppShell screen. */}
            <Route path="/outbox" element={<Scoped><Outbox /></Scoped>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </ScoreProvider>
        </ScribeActiveShowProvider>
      </ScribeActiveOrgProvider>
    </CognitoAuthProvider>
  );
}

export default function App({ cognitoAuthConfig }) {
  const theme = createBrandTheme();
  return (
    <ThemeProvider theme={theme} defaultMode="light">
      <CssBaseline />
      <ReloadPrompt />
      <AppContent cognitoAuthConfig={cognitoAuthConfig} />
      <UsaBadge />
      <BuildBadge />
    </ThemeProvider>
  );
}
